import { forwardRef, useId, useState } from "react";
import { cn } from "../utils/cn";

// ── Inline SVG icons ──────────────────────────────────────────────────────────

function Spinner({ dim }: { dim: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path
        fill="currentColor"
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function CheckIcon({ dim }: { dim: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Static label displayed above the field */
  label?: string;
  /** Supplemental text below the field */
  helperText?: string;
  /** Validation error — replaces helperText and turns the border red */
  error?: string;
  /** Icon rendered inside the left edge of the input */
  prefixIcon?: React.ReactNode;
  /** Icon rendered inside the right edge of the input */
  suffixIcon?: React.ReactNode;
  /** Short text displayed at the left edge, separated by a divider (e.g. "₹", "+91") */
  prefixText?: string;
  /** Shows a spinner in place of the suffix icon */
  isLoading?: boolean;
  /** Marks the field as successfully validated — shows a green checkmark */
  success?: boolean;
  /** Controls height, padding, and font size */
  size?: "sm" | "md" | "lg";
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE = {
  sm: {
    wrapper:   "min-h-[36px]",
    input:     "text-sm",
    padding:   "py-2 px-3",
    iconSize:  14,
    iconInset: "inset-y-0",
    iconLeft:  "left-2.5",
    iconRight: "right-2.5",
    padLeft:   "pl-8",
    padRight:  "pr-8",
  },
  md: {
    wrapper:   "min-h-[40px]",
    input:     "text-base",
    padding:   "py-2.5 px-3",
    iconSize:  16,
    iconInset: "inset-y-0",
    iconLeft:  "left-3",
    iconRight: "right-3",
    padLeft:   "pl-10",
    padRight:  "pr-10",
  },
  lg: {
    wrapper:   "min-h-[48px]",
    input:     "text-lg",
    padding:   "py-3 px-4",
    iconSize:  18,
    iconInset: "inset-y-0",
    iconLeft:  "left-3.5",
    iconRight: "right-3.5",
    padLeft:   "pl-11",
    padRight:  "pr-11",
  },
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      prefixIcon,
      suffixIcon,
      prefixText,
      isLoading = false,
      success = false,
      size = "md",
      className,
      disabled,
      id: idProp,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const [isFocused, setIsFocused] = useState(false);

    const hasError = !!error;
    const showSuccess = success && !hasError && !disabled;
    const s = SIZE[size];

    // ── Border / ring classes ─────────────────────────────────────────────────

    const wrapperBorder = hasError
      ? "border-red-400 dark:border-red-500"
      : showSuccess
        ? "border-green-400 dark:border-green-500"
        : isFocused
          ? "border-brand-purple dark:border-violet-500"
          : "border-border-default dark:border-border-strong";

    const wrapperRing = isFocused
      ? hasError
        ? "ring-2 ring-red-400/20 dark:ring-red-400/30"
        : "ring-2 ring-brand-purple/20 dark:ring-brand-purple/30"
      : "";

    const hasPrefixIcon = !!prefixIcon;
    const hasPrefixText = !!prefixText;
    const hasSuffix = isLoading || !!suffixIcon || showSuccess;

    const inputLeftPad  = hasPrefixText ? "" : hasPrefixIcon ? s.padLeft : "";
    const inputRightPad = hasSuffix ? s.padRight : "";

    // ── Event handlers ────────────────────────────────────────────────────────

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        {/* Label — outside the box, same pattern as Select */}
        {label && (
          <label
            htmlFor={id}
            className="text-xs font-medium text-content-secondary"
          >
            {label}
          </label>
        )}

        {/* Outer wrapper — border, ring, background */}
        <div
          className={cn(
            "flex items-stretch w-full rounded-md border transition-all duration-150 overflow-hidden",
            "bg-white dark:bg-surface-sunken",
            wrapperBorder,
            wrapperRing,
            disabled && "opacity-60 cursor-not-allowed",
          )}
        >
          {/* Prefix text area (e.g. "₹" or "+91") */}
          {hasPrefixText && (
            <div
              className={cn(
                "flex items-center shrink-0 px-3",
                "border-r border-border-default dark:border-border-strong",
                "bg-surface-raised dark:bg-surface-overlay",
                "text-content-secondary text-sm select-none",
              )}
              aria-hidden="true"
            >
              {prefixText}
            </div>
          )}

          {/* Inner area — icons + input */}
          <div className="relative flex-1">
            {/* Prefix icon */}
            {hasPrefixIcon && (
              <span
                className={cn(
                  "absolute flex items-center pointer-events-none",
                  s.iconInset,
                  s.iconLeft,
                  hasError ? "text-red-500 dark:text-red-400" : "text-content-tertiary",
                )}
                aria-hidden="true"
              >
                {prefixIcon}
              </span>
            )}

            {/* Input element */}
            <input
              ref={ref}
              id={id}
              disabled={disabled}
              value={value}
              defaultValue={defaultValue}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              aria-invalid={hasError}
              aria-describedby={
                error ? `${id}-error` : helperText ? `${id}-helper` : undefined
              }
              className={cn(
                "w-full h-full bg-transparent outline-none",
                "text-content-primary placeholder:text-content-tertiary",
                "disabled:cursor-not-allowed",
                s.input,
                s.padding,
                inputLeftPad,
                inputRightPad,
              )}
              {...rest}
            />

            {/* Suffix: spinner > suffixIcon > success checkmark */}
            {hasSuffix && (
              <span
                className={cn(
                  "absolute flex items-center pointer-events-none",
                  s.iconInset,
                  s.iconRight,
                  isLoading
                    ? "text-content-tertiary"
                    : showSuccess
                      ? "text-green-500 dark:text-green-400"
                      : "text-content-tertiary",
                )}
                aria-hidden="true"
              >
                {isLoading ? (
                  <Spinner dim={s.iconSize} />
                ) : showSuccess ? (
                  <CheckIcon dim={s.iconSize} />
                ) : (
                  suffixIcon
                )}
              </span>
            )}
          </div>
        </div>

        {/* Error / helper text */}
        {(error || helperText) && (
          <p
            id={error ? `${id}-error` : `${id}-helper`}
            role={error ? "alert" : undefined}
            className={cn(
              "text-xs leading-snug animate-slide-down",
              error ? "text-red-600 dark:text-red-400" : "text-content-secondary",
            )}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
